import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './context/ThemeContext';
import { ProvideData } from './hooks/useData';
import { LoadingProvider, useLoadingBar } from './context/LoadingContext';
import Layout from './components/Layout';
import ElectricLoadingBar from './components/ElectricLoadingBar';
import usePageTracking from './hooks/usePageTracking';
import './App.css';

// Lazy load all page components
// const Home = lazy(() => import('./pages/Home'));
const ThreeBase = lazy(() => import('./components/3d/ThreeBase'));
// const Wave = lazy(() => import('./pages/Wave'));
// const Vaporwave = lazy(() => import('./pages/Vaporwave'));
// const City = lazy(() => import('./pages/City'));
// const CityFuture = lazy(() => import('./pages/CityFuture'));
// const TokenPanelDemo = lazy(() => import('./components/dex/TokenPanelDemo'));
const MonsterSplash = lazy(() => import('./components/MonsterSplash'));

// Loading component for Suspense fallback - minimal version
const LoadingSpinner = ({ componentName = 'component' }) => {
  const { registerTask, updateTask, completeTask } = useLoadingBar();
  const taskId = `suspense_${componentName}`;

  React.useEffect(() => {
    registerTask(taskId, 0);
    updateTask(taskId, 0, `Loading ${componentName}...`);
    
    // Simulate loading progress
    const progressSteps = [20, 50, 80, 100];
    let currentStep = 0;
    
    const progressInterval = setInterval(() => {
      if (currentStep < progressSteps.length) {
        updateTask(taskId, progressSteps[currentStep], `Loading ${componentName}...`);
        currentStep++;
      } else {
        clearInterval(progressInterval);
        setTimeout(() => completeTask(taskId), 300);
      }
    }, 150);

    return () => {
      clearInterval(progressInterval);
      completeTask(taskId);
    };
  }, [taskId, componentName, registerTask, updateTask, completeTask]);

  // Return null - no visual loading spinner, just track progress
  return null;
};

// Component to handle page tracking
const AppWithTracking = () => {
  usePageTracking();
  
  return (
    <Suspense fallback={<LoadingSpinner componentName="MonsterSplash" />}>
      <Routes>
        <Route path="/" element={<MonsterSplash />} />
        <Route path="/portal" element={<ThreeBase />} />
        {/* Catch-all route - redirect any unknown routes to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
        {/* Commented out unused routes for bundle optimization */}
        {/* <Route path="/*" element={
          <Layout>
            <Suspense fallback={<LoadingSpinner componentName="Page" />}>
              <Routes>
                <Route path="/home" element={<Home />} />
                <Route path="/wave" element={<Wave />} />
                <Route path="/vaporwave" element={<Vaporwave />} />
                <Route path="/city" element={<City />} />
                <Route path="/city-future" element={<CityFuture />} />
                <Route path="/token/panel" element={<TokenPanelDemo />} />
              </Routes>
            </Suspense>
          </Layout>
        } /> */}
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <ProvideData>
          <LoadingProvider>
            <Router>
              <AppWithTracking />
              <ElectricLoadingBar />
            </Router>
          </LoadingProvider>
        </ProvideData>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
